import styled, { css, DefaultTheme } from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";

export type V3FeedRowIconTone =
  | "success"
  | "warning"
  | "neutral"
  | "info"
  | "error";

export type V3FeedRowAmountTone = "positive" | "negative" | "neutral";

const iconToneTile = (theme: DefaultTheme, tone: V3FeedRowIconTone): string => {
  if (theme.mode === "dark") return "transparent";
  const tiles = theme.colors.domainSpecific.timelineTiles;
  switch (tone) {
    case "success":
      return tiles.success;
    case "warning":
      return tiles.warning;
    case "info":
      return tiles.info;
    case "error":
      return tiles.danger;
    default:
      return tiles.neutral;
  }
};

const iconToneGlyph = (
  theme: DefaultTheme,
  tone: V3FeedRowIconTone
): string => {
  const icon = theme.colors.ui.icon;
  switch (tone) {
    case "success":
      return icon.success;
    case "warning":
      return icon.warning;
    case "info":
      return icon.info;
    case "error":
      return icon.error;
    default:
      return icon.secondary;
  }
};

// The numbers scale is tuned for light surfaces, so an unchanged amount reads as
// near-black on the dark page. Track the body text instead, as mobile V3 does.
const amountToneColor = (
  theme: DefaultTheme,
  tone: V3FeedRowAmountTone
): string => {
  const text = theme.colors.ui.text;
  switch (tone) {
    case "positive":
      return text.success;
    case "negative":
      return text.error;
    default:
      return text.primary;
  }
};

const surface = css`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md}px;
  width: 100%;
  text-align: left;
  background: ${({ theme }) =>
    theme.mode === "dark"
      ? theme.colors.ui.background.page
      : theme.colors.ui.background.raised};
  border: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) =>
      theme.mode === "dark"
        ? theme.colors.ui.border.default
        : theme.colors.ui.border.subtle};
  border-radius: ${({ theme }) => theme.radius.md}px;
  padding: ${({ theme }) => theme.space.card.inset}px
    ${({ theme }) => theme.space.lg}px;
`;

export const FeedRowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm}px;
`;

export const FeedRowOverline = styled.span`
  ${typographyCSS("caption.sm-medium")}
  font-weight: 600;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.ui.text.tertiary};
`;

export const FeedRowSurface = styled.div`
  ${surface}
`;

export const FeedRowButton = styled.button`
  ${surface}
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.duration.instant}ms
    ${({ theme }) => theme.motion.easing.standard};

  &:hover {
    border-color: ${({ theme }) => theme.colors.ui.border.default};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.stroke.focus}px solid
      ${({ theme }) => theme.colors.ui.border.focus};
    outline-offset: ${({ theme }) => theme.space["3xs"]}px;
  }
`;

export const FeedRowIconTile = styled.span<{ $tone: V3FeedRowIconTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ theme }) => theme.size.icon.xl}px;
  height: ${({ theme }) => theme.size.icon.xl}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme, $tone }) => iconToneTile(theme, $tone)};
  color: ${({ theme, $tone }) => iconToneGlyph(theme, $tone)};
`;

export const FeedRowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  min-width: 0;
`;

export const FeedRowTitle = styled.span`
  ${typographyCSS("label.sm-semibold")}
  letter-spacing: -0.1px;
  color: ${({ theme }) => theme.colors.ui.text.primary};
`;

export const FeedRowSubtitle = styled.span`
  ${typographyCSS("caption.sm")}
  color: ${({ theme }) => theme.colors.ui.text.tertiary};
`;

export const FeedRowAmount = styled.span<{ $tone: V3FeedRowAmountTone }>`
  margin-left: auto;
  padding-left: ${({ theme }) => theme.space.sm}px;
  white-space: nowrap;
  ${typographyCSS("number.md")}
  color: ${({ theme, $tone }) => amountToneColor(theme, $tone)};
`;

export const FeedRowSkeletonTile = styled.span`
  flex: none;
  width: ${({ theme }) => theme.size.icon.xl}px;
  height: ${({ theme }) => theme.size.icon.xl}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme }) => theme.colors.ui.background.inverseSecondary};
  opacity: ${({ theme }) => theme.opacity["loading-surface"]};
`;

export const FeedRowSkeletonLine = styled.span<{ $width: number }>`
  display: block;
  width: ${({ $width }) => $width}%;
  height: ${({ theme }) => theme.size.indicator.skeleton.thick}px;
  border-radius: ${({ theme }) => theme.radius.full}px;
  background: ${({ theme }) => theme.colors.ui.background.inverseSecondary};
  opacity: ${({ theme }) => theme.opacity["loading-line"]};
`;
